import { useCallback, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { OrderBookLadder } from "../types/market";
import { useApiAuth } from "./useApiAuth";
import { apiUrl } from "../lib/api";

type UseOrderBookLadderArgs = {
  competitionCode: string;
  seasonCode: string;
  teamCode: string;
  poolCode: string;
  enabled?: boolean;
  limit?: number;
  onWebSocketUpdate?: (callback: (ladder: OrderBookLadder) => void) => void;
};

type UseOrderBookLadderResult = {
  ladder: OrderBookLadder | null;
  isLoading: boolean;
  isError: boolean;
  reload: () => Promise<void>;
};

export function useOrderBookLadder({
  competitionCode,
  seasonCode,
  teamCode,
  poolCode,
  enabled = true,
  limit = 10,
  onWebSocketUpdate,
}: UseOrderBookLadderArgs): UseOrderBookLadderResult {
  const { acquireToken } = useApiAuth();
  const queryClient = useQueryClient();
  const canLoad = Boolean(enabled && teamCode);
  const queryKey = useMemo(
    () => ["orderbook-ladder", competitionCode, seasonCode, teamCode, poolCode, limit],
    [competitionCode, seasonCode, teamCode, poolCode, limit],
  );

  const query = useQuery({
    queryKey,
    enabled: canLoad,
    queryFn: async () => {
      const accessToken = await acquireToken();
      if (!accessToken) {
        throw new Error("Authentication required.");
      }

      const url = apiUrl(
        `/api/competitions/${competitionCode}/seasons/${seasonCode}/teams/${teamCode}/orderbook?pool_code=${poolCode}&limit=${limit}`,
      );
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch order book ladder (${response.status})`);
      }

      const data = await response.json();
      return (data.ladder ?? null) as OrderBookLadder | null;
    },
  });

  const handleUpdate = useCallback(
    (newLadder: OrderBookLadder) => {
      queryClient.setQueryData(queryKey, newLadder);
    },
    [queryClient, queryKey],
  );

  useEffect(() => {
    if (!enabled || !onWebSocketUpdate) {
      return;
    }

    onWebSocketUpdate(handleUpdate);
  }, [enabled, onWebSocketUpdate, handleUpdate]);

  return {
    ladder: canLoad ? query.data ?? null : null,
    isLoading: canLoad ? query.isLoading : false,
    isError: canLoad ? query.isError : false,
    reload: async () => {
      await query.refetch();
    },
  };
}
